import {
  TableCollapsable,
  Button,
  TablePagination,
  addToast,
  showDialogConfirm,
} from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { useCallback, useRef, useState } from 'react';
import { Card, Col, Row } from 'reactstrap';
import { TranslationsKeys } from '../../../locales/translations';
import { useAdminColumns } from '../hooks/useAdminColumns';
import AnnouncementDetails from './AnnouncementDetails';
import { useAnnouncementAdminListLoader } from './Loaders/AnnouncementAdminListLoader';
import { Announcement } from '../types';
import AnnouncementEditModal, {
  AnnouncementEditModalMethods,
} from './AnnouncementEditModal';
import { remove } from '../api';

const AnnouncementAdminTable = () => {
  const { t } = useTranslation(
    TranslationsKeys.MANAGEMENT_TOOLS_ANNOUNCEMENTS_ADMIN_LIST,
  );

  const modalEdit = useRef<AnnouncementEditModalMethods>(null);

  const [deleting, setDeleting] = useState(false);

  const {
    data,
    query,
    loading,
    updateQuery,
  } = useAnnouncementAdminListLoader();

  const handlePressEdit = useCallback((announcement: Announcement) => {
    modalEdit?.current?.open(announcement);
  }, []);

  const handleDelete = useCallback(
    async (announcement: Announcement) => {
      if (deleting) {
        return;
      }
      setDeleting(true);
      const { error } = await remove(announcement.id);
      setDeleting(false);
      if (error) {
        return addToast({
          icon: 'error',
          color: 'danger',
          text: t('delete.failed'),
        });
      } else {
        addToast({
          icon: 'success',
          color: 'success',
          text: t(`delete.success`),
        });
        updateQuery({ ...query, page: 1 });
      }
    },
    [deleting, query, updateQuery, t],
  );
  const handlePressDelete = useCallback(
    (announcement: Announcement) => {
      if (deleting) {
        return;
      }
      showDialogConfirm({
        icon: {
          name: 'warning',
          color: 'warning',
        },
        title: t(`delete.title`),
        subtitle: t(`delete.subtitle`),
        btnConfirm: {
          text: t(`common:actions.confirm`),
          onConfirm: () => handleDelete(announcement),
        },
        btnClose: {
          text: t(`common:actions.cancel`),
        },
      });
    },
    [deleting, handleDelete, t],
  );

  const handlePressAdd = useCallback(() => {
    modalEdit?.current?.open();
  }, []);

  const columns = useAdminColumns({
    onPressEdit: handlePressEdit,
    onPressDelete: handlePressDelete,
  });

  return (
    <>
      <div className="mx-3">
        <Card className="p-4">
          <Row>
            <Col xs={12}>
              <p className="fs-14 W-100">{t(`description`)}</p>
              <Button
                text={t(`create`)}
                icon="plus"
                size="md"
                type="button"
                onClick={handlePressAdd}
              />
            </Col>
            <Col xs={12} className="mt-4">
              <TableCollapsable
                columns={columns}
                data={data?.data || []}
                isLoading={loading || deleting}
                noResultsText={t('empty')}
                secondColumn={{
                  columnName: 'second',
                  headerText: 'second',
                  cellFormat: ({ row }) => (
                    <AnnouncementDetails
                      announcement={row}
                      translationKey={
                        TranslationsKeys.MANAGEMENT_TOOLS_ANNOUNCEMENTS_ADMIN_LIST
                      }
                    />
                  ),
                }}
              />
              {!!(data?.total_pages && data?.total_pages > 1) && (
                <TablePagination
                  pagination={{
                    currentPage: query.page,
                    itemsPerPage: query.items_per_page,
                    onChangePage: (page) => {
                      updateQuery({ ...query, page: page });
                    },
                    totalItems: data?.total || 0,
                    totalPages: data?.total_pages || 0,
                  }}
                />
              )}
            </Col>
          </Row>
        </Card>
      </div>
      <AnnouncementEditModal
        ref={modalEdit}
        onSave={() => updateQuery({ ...query, page: 1 })}
      />
    </>
  );
};

export default AnnouncementAdminTable;
